import { useLocation, useParams } from 'react-router-dom';
import React, { useContext } from 'react';
import { AuthContext } from '../../contextProviders/AuthContext';
import strings from '../../strings/strings.json';
import useGetRequest from '../../hooks/useGetRequest';
import { PublishedCourse } from '../../models/Course';
import { ActivityType } from '../../models/Activity';
import { MainSectionWithGaps } from '../../components/sections/MainSection';
import Stepper from '../../components/stepper/Stepper';
import SelectCourseDateForm from './components/SelectCourseDateForm';
import SelectTrainerForm from './components/SelectTrainerForm';
import { ConfigureMeetingsForm } from './components/ConfigureMeetingsForm';
import ReviewScheduleForm from './components/ReviewScheduleForm';
import { StepInput } from '../../components/stepper/stepperTypes';
import { RoleType } from '../../models/User';
import { LocationType } from '../../models/ActivityMeeting';
import SelectNumberOfLearnersForm from './components/SelectNumberOfLearners';
import Header from 'components/sections/Header';
import NavBackButton from 'components/buttons/NavBackButton';

export type NewMeeting = {
    activityId: string;
    title: string;
    moduleName: string;
    order: string;
    startDate: Date | undefined;
    startTime: Date | undefined;
    durationInMins: number | null;
    locationType: LocationType | undefined;
    location: string | undefined;
    joinLink: string | undefined;
};

export type CourseToBeScheduled = {
    courseId: string;
    courseName: string;
    courseStartDate: Date | undefined;
    courseEndDate: Date | undefined;
    meetings: NewMeeting[];
    trainerId: string | undefined;
    trainerName: string | undefined;
    minimumLearners: number;
    maximumLearners: number;
    organisationNumberOfLiveCourses: number;
    courseIsFree: boolean;
};

type State = {
    backUrl: string;
};

export default function ScheduleCoursePage(): JSX.Element {
    const params = useParams();
    const { courseId } = params;
    const { userData } = useContext(AuthContext);

    const { state } = useLocation();
    const { backUrl } = (state as State) ?? {};

    const [schedule, setSchedule] = React.useState<CourseToBeScheduled | undefined>();

    const { data: course, loading, errors } = useGetRequest<PublishedCourse>(`courses/${courseId}`, !courseId);

    const backButtonDetails = React.useMemo(() => {
        return {
            title: strings.courseSchedulePage.backButtonTitle,
            aria: strings.courseSchedulePage.backButtonTitleAria,
            href: backUrl,
        };
    }, [backUrl]);

    React.useEffect(() => {
        if (!course) return;

        let meetings: NewMeeting[] = [];

        course.modules.forEach((module) => {
            if (module.activities.some((a) => a.type === ActivityType.LIVE_TRAINING)) {
                module.activities.forEach((activity) => {
                    if (activity.type === ActivityType.LIVE_TRAINING) {
                        meetings.push({
                            activityId: activity.id,
                            title: activity.title,
                            moduleName: module.title,
                            order: module.order.toString() + activity.order.toString(),
                            startDate: undefined,
                            joinLink: undefined,
                            location: undefined,
                            startTime: undefined,
                            durationInMins: activity.durationInMins,
                            locationType: undefined,
                        });
                    }
                });
            }
        });

        let trainerId = undefined;
        let trainerName = undefined;

        if (!userData.roles.includes(RoleType.ORGANISATION_ADMIN)) {
            trainerId = userData.id;
            trainerName = `${userData.firstName} ${userData.lastName}`;
        }

        setSchedule({
            courseId: course.id,
            courseName: course.title,
            courseStartDate: undefined,
            courseEndDate: undefined,
            meetings: meetings,
            trainerId: trainerId,
            trainerName: trainerName,
            minimumLearners: 1,
            maximumLearners: 10,
            organisationNumberOfLiveCourses: course.organisationNumberOfLiveCourses,
            courseIsFree: course.courseIsFree,
        });
    }, [course]); //eslint-disable-line

    if (!course || loading || !schedule) return <></>;

    const steps: StepInput[] = [
        { name: 'Course Date', Form: SelectCourseDateForm },
        { name: 'Learners', Form: SelectNumberOfLearnersForm },
    ];

    if (userData.roles.includes(RoleType.ORGANISATION_ADMIN)) steps.push({ name: 'Trainer', Form: SelectTrainerForm });

    const hasMeetings = course.modules
        .flatMap((module) => module.activities)
        .some((activity) => ActivityType.LIVE_TRAINING === activity.type);

    if (hasMeetings) steps.push({ name: 'Meetings', Form: ConfigureMeetingsForm });

    steps.push({ name: 'Review', Form: ReviewScheduleForm });

    if (errors && errors.length) return <></>;

    return (
        <>
            <Header
                title={`${strings.courseSchedulePage.title} ${course.title}`}
                aboveTitleElement={
                    <NavBackButton
                        backButtonTitle={backButtonDetails.title}
                        backButtonAria={backButtonDetails.aria}
                        backButtonHref={backButtonDetails.href}
                    />
                }
            />
            <MainSectionWithGaps>
                <Stepper<CourseToBeScheduled> defaultObject={schedule} steps={steps} />
            </MainSectionWithGaps>
        </>
    );
}
